import React, { useState, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { Link } from "react-router-dom";

import useErrorHandler, {
  ApplicationError,
} from "../../../services/error-handler/ErrorHandlerService";
import { snackbarNotificationsService } from "../../../services/notifications/SnackbarNotificationsService";
import PageLayout from "../../_layout/PageLayout";
import { ManageDeletedData } from "../../../services/authorization/Permissions";
import { permissionsService } from "../../../services/authorization/UserPermissionsService";
import { api } from "../../../services/api/ApiService";
import { SettingsMenuItemsModel } from "../SettingsMenuItems";
import { DeletedDataStorageModel } from "../../../services/organization/DeletedDataStorage";
import DataGrid from "../../../components/datatable/DataGrid";
import {
  ActionsSection,
  ButtonsSection,
} from "../../../components/data-components/sections/DataSections";
import RestoreButton from "./components/RestoreButton";
import { DeletedDataItem } from "../../../services/organization/DeletedDataItem";
import DeleteButton from "../../../components/buttons/delete-button/DeleteButton";
import { modalNotificationsService } from "../../../services/notifications/ModalNotificationService";
import { translationService } from "../../../services/translation/TranslationService";
import { RestoreResponse } from "../../../api-models/response/organization/RestoreResponse";

export default observer(() => {
  const { handleErrorInSnackbar } = useErrorHandler();

  const [settingsMenuItems] = useState(() => SettingsMenuItemsModel.create({}));

  const [deletedDataStorage] = useState(() =>
    DeletedDataStorageModel.create({ clientSidePagination: true, useGridFiltering: true }, { api })
  );

  const menuItems = {
    staticItems: settingsMenuItems.getStaticItems(),
  };

  const isRestoreButtonDisabled = useCallback(
    () =>
      !permissionsService.userHasPermissionTo(ManageDeletedData) ||
      !deletedDataStorage.anyItemsSelected ||
      deletedDataStorage.isRestoreInProgress,
    [deletedDataStorage.anyItemsSelected, deletedDataStorage.isRestoreInProgress]
  );

  const getSelectedItems = useCallback(() => {
    return deletedDataStorage.selectedItems.map((id) => {
      return deletedDataStorage.data.find((i: DeletedDataItem) => i.id === id) as DeletedDataItem;
    });
  }, [deletedDataStorage.data, deletedDataStorage.selectedItems]);

  const getRestoreErrorsListElement = useCallback(
    (restoreErrors: RestoreResponse[]) => {
      const differentErrors = Array.from(new Set(restoreErrors.map((x) => x.messageKey)));
      return (
        <>
          {differentErrors.map((errorMessage) => {
            return (
              <div key={errorMessage}>
                {translationService.translate(errorMessage)}
                <ul>
                  {restoreErrors
                    .filter((x) => x.messageKey === errorMessage)
                    .map((restoreError) => {
                      const item = (deletedDataStorage.data as DeletedDataItem[]).find(
                        (deletedItem) =>
                          deletedItem.type === restoreError.entityTypeName &&
                          deletedItem.identification === restoreError.entityId
                      );
                      return (
                        <li key={restoreError.entityId}>
                          {item?.displayValues.dataType.toDisplay ?? ""}{" "}
                          {item?.displayValues.identifier.toDisplay ?? ""}
                          {translationService.translate("general.to")}{" "}
                          <Link
                            to={`${item?.details.path}`}
                            onClick={() => modalNotificationsService.closeModalNotification()}
                          >
                            {item?.details.toDisplay}
                          </Link>
                        </li>
                      );
                    })}
                </ul>
              </div>
            );
          })}
        </>
      );
    },
    [deletedDataStorage.data]
  );

  const handleRestoreAction = useCallback(async () => {
    try {
      const selectedItems = getSelectedItems().map((x) => ({
        id: x.identification,
        type: x.type,
      }));
      await deletedDataStorage.restoreDeletedData(selectedItems);

      if (deletedDataStorage.errorRestoreMessages.length > 0) {
        modalNotificationsService.showError({
          titleKey: translationService.translate("DeletedData.restore-data.modal-error"),
          errorElement: getRestoreErrorsListElement(deletedDataStorage.errorRestoreMessages),
        });
        return;
      }

      if (deletedDataStorage.warningRestoreMessages.length > 0) {
        deletedDataStorage.warningRestoreMessages.forEach((m) =>
          snackbarNotificationsService.showSuccess(m)
        );
      }

      snackbarNotificationsService.showSuccess("DeletedData.restore-data.success");
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withErrorMessage("DeletedData.restore-data.default-error");
      handleErrorInSnackbar(applicationError);
    }
  }, [deletedDataStorage, getRestoreErrorsListElement, getSelectedItems, handleErrorInSnackbar]);

  const isDeleteButtonDisabled =
    !permissionsService.userHasPermissionTo(ManageDeletedData) ||
    !deletedDataStorage.anyItemsSelected ||
    deletedDataStorage.isDeleteInProgress;

  const onHardDeleteSubmit = useCallback(async () => {
    try {
      const selectedItems = getSelectedItems().map((x) => ({
        id: x.identification,
        type: x.type,
      }));
      await deletedDataStorage.hardDeleteData(selectedItems);

      snackbarNotificationsService.showSuccess("DeletedData.hard-delete.success");
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withErrorMessage("default-error");
      handleErrorInSnackbar(applicationError);
    }
  }, [deletedDataStorage, getSelectedItems, handleErrorInSnackbar]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[ManageDeletedData]}
      id="manage-deleted-data"
      titleKey="DeletedData.title"
      menuItems={menuItems}
    >
      <ActionsSection>
        <ButtonsSection>
          <RestoreButton
            confirmationAction={handleRestoreAction}
            confirmationModalTitleKey="DeletedData.restore-data.title"
            disabled={isRestoreButtonDisabled()}
          />
          <DeleteButton
            confirmationModalTitleKey={"DeletedData.hard-delete-title"}
            confirmationModalTitleContext={{
              numberOfItems: deletedDataStorage.selectedItems.length,
            }}
            confirmationAction={onHardDeleteSubmit}
            confirmationModalSubtitleKey={"DeletedData.hard-delete-subtitle"}
            disabled={isDeleteButtonDisabled}
          />
        </ButtonsSection>
      </ActionsSection>
      <DataGrid
        itemTypeKey="general.deleted-data"
        dataGridStorage={deletedDataStorage}
        noResultsMessageKey="DeletedData.empty-grid"
      />
    </PageLayout>
  );
});
